import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import healthStorage from '../services/healthStorage';
import apiService from '../services/apiService';
import backupSyncService from '../services/backupSyncService';
import { redactText } from '../utils/redactionEngine';
import { logPrivacyEvent } from '../utils/privacyGuardLog';
import { RedactionReviewModal, isReviewGateEnabled } from '../components/RedactionReviewModal';

const logger = console; // Simple logger

/**
 * PWA Phase 0 (May 7, 2026): fire-and-forget telemetry to the unified
 * governance_events trail. Never throws, never blocks the caller, never
 * surfaces errors to the UI. See PWA_DESIGN.md §Data Model and
 * PWA_ADOPTION_METRICS_DESIGN.md §Event emission ownership.
 *
 * Allowed event_type values are enforced server-side
 * (ALLOWED_PWA_EVENT_TYPES in backend/server.py).
 */
async function _logPwaEvent(eventType, sessionToken, extraDetails) {
  try {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    if (!apiBase) return;
    let quotaBytes = null;
    let usageBytes = null;
    try {
      if (navigator?.storage?.estimate) {
        const est = await navigator.storage.estimate();
        quotaBytes = est.quota ?? null;
        usageBytes = est.usage ?? null;
      }
    } catch (_) { /* swallow */ }
    const details = {
      user_agent: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 256),
      storage_quota_bytes: quotaBytes,
      storage_usage_bytes: usageBytes,
      ...(extraDetails || {}),
    };
    await fetch(`${apiBase}/api/governance/pwa-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        session_token: sessionToken || null,
        details,
      }),
      keepalive: true,
    });
  } catch (_) {
    /* telemetry must never break the storage flow */
  }
}

const HealthContext = createContext();

export const useHealth = () => {
  const context = useContext(HealthContext);
  if (!context) {
    throw new Error('useHealth must be used within HealthProvider');
  }
  return context;
};

export const HealthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [healthRecords, setHealthRecords] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [storageStats, setStorageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [loggedOutReason, setLoggedOutReason] = useState(null); // 'expired' | 'another_device' | null
  
  // Persistent storage protection prompt state
  const [showProtectionPrompt, setShowProtectionPrompt] = useState(false);
  const [storageProtected, setStorageProtected] = useState(null);
  
  // Storage optimization notification (Self-Healing Storage - Feb 3, 2026)
  const [storageOptimized, setStorageOptimized] = useState(false);

  // Redaction review gate (show-and-tell) — see RedactionReviewModal
  const [redactionGate, setRedactionGate] = useState(null); // { redactedText, removedItems }
  const redactionGateResolver = useRef(null);

  // Opens the review modal and resolves with the (possibly edited) text, or null if cancelled.
  const openRedactionReview = (redactedText, removedItems) => new Promise((resolve) => {
    redactionGateResolver.current = resolve;
    setRedactionGate({ redactedText, removedItems });
  });
  const resolveRedactionReview = (result) => {
    setRedactionGate(null);
    const r = redactionGateResolver.current;
    redactionGateResolver.current = null;
    if (r) r(result);
  };

  // Public: run the show-and-tell review gate on any outbound text (chat or
  // document analyze). Returns { text, reviewed } or { cancelled:true }.
  const reviewRedaction = async (rawText, { source } = {}) => {
    if (!rawText || source === 'timeline' || !isReviewGateEnabled()) {
      return { text: rawText, reviewed: false };
    }
    const preview = redactText(rawText);
    if (!preview.totalRedacted) return { text: rawText, reviewed: false };
    const confirmed = await openRedactionReview(preview.redactedText, preview.blockedFragments || []);
    if (confirmed == null) return { cancelled: true };
    return { text: confirmed, reviewed: true };
  };
  
  // User preferences for personalization (stored locally)
  const [userPreferences, setUserPreferences] = useState({
    preferredName: '',
    firstVisitComplete: false,
    visitCount: 0,
    userAvatar: '👤',
    drBenAvatar: '👨‍⚕️'
  });

  // Check session validity periodically
  const checkSessionValidity = useCallback(async () => {
    if (!user) return;
    
    try {
      const status = await apiService.getSessionStatus();
      
      // SECURITY: On network error, log user out after grace period
      // Don't allow indefinite offline access
      if (status.network_error) {
        console.warn('[Session] Network error checking session - will retry');
        // Allow a few retries before forcing logout
        // For now, keep user logged in but log the issue
        return;
      }
      
      if (!status.valid) {
        logger.warn('Session invalid:', status.logged_out_reason);
        setSessionExpired(true);
        setLoggedOutReason(status.logged_out_reason || 'expired');
        // Auto-logout on session expiry
        await healthStorage.deleteLocalUser();
        setUser(null);
      }
    } catch (error) {
      // Network error during periodic check - log but don't immediately logout
      // This is less critical than startup check
      console.error('Session check failed (network issue):', error);
    }
  }, [user]);

  // Set up session check interval (every 30 seconds for faster detection)
  useEffect(() => {
    if (user) {
      // Check immediately on user change
      checkSessionValidity();
      // Then check every 30 seconds
      const interval = setInterval(checkSessionValidity, 30 * 1000);
      return () => clearInterval(interval);
    }
  }, [user, checkSessionValidity]);

  // Initialize on mount
  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      // ========================================
      // SELF-HEALING STORAGE CHECK (Feb 3, 2026)
      // Apple Design Philosophy: "Proactive, Not Reactive"
      // Detect and repair corrupted IndexedDB BEFORE it causes issues
      // ========================================
      const storageHealth = await healthStorage.initializeWithHealthCheck();
      if (storageHealth.wasRepaired) {
        console.warn('[HealthContext] Storage was corrupted and auto-repaired. User data cleared.');
        // Storage was cleared - user will need to re-login and restore from backup
        // Show subtle "Storage optimized" notification (Apple: "delightful surprises")
        setStorageOptimized(true);
        // Auto-dismiss after 5 seconds
        setTimeout(() => setStorageOptimized(false), 5000);
      }
      if (!storageHealth.healthy) {
        console.error('[HealthContext] Storage is unavailable - app will run in session-only mode');
        // Continue anyway - non-blocking approach
      }
      
      // Check if persistent storage is already granted (don't auto-request)
      // Prompt will appear after first record upload (Option A) or in Profile (Option C)
      const isPersisted = await healthStorage.isPersisted();
      setStorageProtected(isPersisted);
      
      // Load local user from storage first
      const localUser = await healthStorage.getLocalUser();
      
      if (localUser) {
        // User was logged in previously - verify with backend
        try {
          // First check session status
          const sessionStatus = await apiService.getSessionStatus();
          
          // SECURITY: On network error, DO NOT auto-login - require re-authentication
          if (sessionStatus.network_error) {
            console.warn('[Session] Network error during startup - requiring re-login for security');
            // Don't auto-login - user must authenticate again
            // Keep local data but don't set user state
            logger.info('Session verification failed (network) - login required');
          } else if (sessionStatus.valid && !sessionStatus.expired) {
            const backendUser = await apiService.getCurrentUser();
            if (backendUser) {
              // Session still valid - restore user
              setUser({
                email: backendUser.email,
                role: backendUser.role,
                user_id: backendUser.user_id,
                email_verified: backendUser.email_verified,
                created_at: backendUser.created_at
              });
              logger.info('Session restored from backend');
              
              // Refresh session to extend expiry
              await apiService.refreshSession();
            } else {
              // Backend returned no user - require login
              console.warn('[Session] No backend user - login required');
              await healthStorage.deleteLocalUser();
              setUser(null);
            }
          } else {
            // Session genuinely expired (confirmed by server) - clear local user
            console.warn('Session expired (confirmed by server), clearing local user');
            await healthStorage.deleteLocalUser();
            setUser(null);
            setSessionExpired(true);
          }
        } catch (error) {
          // SECURITY: On error, DO NOT auto-login - require re-authentication
          console.warn('[Session] Error during session check - login required:', error.message);
          // Don't auto-login from local storage - security risk
          logger.info('Session verification failed - login required');
        }
      }

      // Create/get session token
      const token = await apiService.getOrCreateSession();
      setSessionToken(token);

      // Load health records
      const records = await healthStorage.getAllHealthRecords();
      setHealthRecords(records);

      // Load conversations
      const convs = await healthStorage.getAllConversations();
      setConversations(convs);

      // Get storage stats
      const stats = await healthStorage.getStorageStats();
      setStorageStats(stats);
      
      // Load user preferences (personalization)
      const prefs = await healthStorage.getUserPreferences();
      setUserPreferences(prefs);
      
      // Increment visit count
      await healthStorage.incrementVisitCount();

      setLoading(false);
      
      // ========================================
      // BACKUP SYNC CHECK (Simple & Robust)
      // ========================================
      // Check if we have unbackuped records.
      // This runs on every app load - catches any missed backups
      // from logouts, crashes, network issues, etc.
      // 
      // Simple logic: records > last_backup_count? → backup needed
      // ========================================
      const userLoggedIn = localUser && token;
      console.log('[HealthContext] App initialized, checking backup sync eligibility:', {
        hasLocalUser: !!localUser,
        hasToken: !!token,
        willRunBackupSync: userLoggedIn
      });
      
      if (userLoggedIn) {
        setTimeout(async () => {
          try {
            console.log('[HealthContext] Running backup sync check...');
            const backupResult = await backupSyncService.syncBackupIfNeeded(token);
            console.log('[HealthContext] Backup sync result:', backupResult);
            if (backupResult.triggered && backupResult.success) {
              logger.info('[HealthContext] Backup sync completed:', backupResult.records_backed_up, 'records');
            } else if (backupResult.triggered && !backupResult.success) {
              logger.warn('[HealthContext] Backup sync attempted but failed:', backupResult.error);
            }
          } catch (err) {
            logger.warn('[HealthContext] Backup sync check failed:', err.message);
          }
        }, 3000); // Wait 3 seconds after app load for smooth UX
      }
    } catch (error) {
      console.error('Failed to initialize app:', error);
      setLoading(false);
    }
  };

  // ========== USER AUTH (LOCAL + BACKEND) ==========
  
  const loginLocal = async (email, password, forceLogout = false, totpCode = null) => {
    try {
      // Call backend login
      const response = await apiService.login(email, password, forceLogout, totpCode);
      if (response.requires_2fa) {
        return { success: false, requires_2fa: true, error: response.message };
      }
      
      console.log('Login response:', response); // Debug log
      
      // Handle session conflict (another device is active)
      if (response.error === 'session_conflict') {
        console.log('Session conflict detected, returning conflict info'); // Debug log
        return { 
          success: false, 
          error: 'session_conflict',
          message: response.message,
          hint: response.hint,
          active_since: response.active_since
        };
      }
      
      console.log('[LOGIN_DEBUG] response.success =', response.success, 'type:', typeof response.success);
      if (response.success) {
        console.log('[LOGIN_DEBUG] Success branch entered');
        // Store user info locally
        const userData = {
          email: response.email,
          role: response.role,
          user_id: response.user_id,
          email_verified: response.email_verified,
          created_at: response.created_at,
          last_backup_date: response.last_backup_date,
          last_backup_records: response.last_backup_records
        };
        console.log('[LOGIN_DEBUG] About to save user data:', userData);
        try {
          await healthStorage.saveLocalUser(userData);
          console.log('[LOGIN_DEBUG] User data saved successfully');
        } catch (storageError) {
          // Storage failure should NOT block login - log and continue
          console.error('[LOGIN_DEBUG] Storage error (non-blocking):', storageError);
          console.warn('[LOGIN_DEBUG] Continuing login despite storage error');
        }
        setUser(userData);
        setSessionExpired(false);
        console.log('[LOGIN_DEBUG] User state updated');
        
        // ========================================
        // BACKUP DATE MERGE - First Principles (Jan 26, 2026)
        // ========================================
        // Problem: Server may have no backup date, but local IndexedDB does
        // Solution: Merge local + server, use MOST RECENT date
        // Never overwrite valid local evidence with empty server data
        // NOTE: This is non-blocking - storage errors won't prevent login
        // ========================================
        try {
          const localPrefs = await healthStorage.getUserPreferences();
          const localBackupDate = localPrefs?.lastBackupDate;
          const serverBackupDate = response.last_backup_date;
          
          let bestBackupDate = null;
          
          if (localBackupDate && serverBackupDate) {
            // Both exist - use most recent
            const localTime = new Date(localBackupDate).getTime();
            const serverTime = new Date(serverBackupDate).getTime();
            bestBackupDate = localTime > serverTime ? localBackupDate : serverBackupDate;
            console.log('[HealthContext] Backup date merge: local=', localBackupDate, 'server=', serverBackupDate, 'using=', bestBackupDate);
          } else if (localBackupDate) {
            // Only local exists - preserve it
            bestBackupDate = localBackupDate;
            console.log('[HealthContext] Preserving local backup date:', localBackupDate);
          } else if (serverBackupDate) {
            // Only server exists - use it
            bestBackupDate = serverBackupDate;
            console.log('[HealthContext] Using server backup date:', serverBackupDate);
          }
          
          // Update local storage with best date (if we have one)
          if (bestBackupDate) {
            await healthStorage.updateUserPreferences({
              lastBackupDate: bestBackupDate
            });
          }
          
          // Sync preferred name from server (source of truth)
          // Ensures greeting works on new devices / incognito / after clear
          if (response.preferred_name) {
            const currentPrefs = await healthStorage.getUserPreferences();
            if (!currentPrefs?.preferredName || currentPrefs.preferredName !== response.preferred_name) {
              await healthStorage.saveUserPreferences({
                preferredName: response.preferred_name
              });
              setUserPreferences(prev => ({ ...prev, preferredName: response.preferred_name }));
            }
          }
        } catch (prefError) {
          console.warn('[LOGIN_DEBUG] Preferences sync failed (non-blocking):', prefError);
        }
        
        // ========================================
        // BACKUP SYNC CHECK ON LOGIN
        // ========================================
        // User just logged in - check if we have unbackuped records
        // This catches the case where:
        // - User uploads data while logged out
        // - User was logged out during upload
        // - Previous backup failed silently
        // ========================================
        if (sessionToken) {
          setTimeout(async () => {
            try {
              console.log('[HealthContext] Running backup sync after login...');
              const backupResult = await backupSyncService.syncBackupIfNeeded(sessionToken);
              if (backupResult.triggered) {
                if (backupResult.success) {
                  console.log('[HealthContext] Post-login backup completed:', backupResult.records_backed_up, 'records');
                } else {
                  console.warn('[HealthContext] Post-login backup failed:', backupResult.error);
                }
              } else {
                console.log('[HealthContext] No backup needed after login:', backupResult.reason);
              }
            } catch (err) {
              console.warn('[HealthContext] Post-login backup sync failed:', err.message);
            }
          }, 2000); // Wait 2 seconds after login for smooth UX
        }
        
        console.log('[LOGIN_DEBUG] Returning success');
        return { success: true, force_login_used: response.force_login_used };
      } else {
        console.log('[LOGIN_DEBUG] Else branch - response was:', JSON.stringify(response));
        return { success: false, error: response.message || response.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('[LOGIN_DEBUG] Catch block entered, error:', error);
      console.error('[LOGIN_DEBUG] error.response:', error.response);
      console.error('[LOGIN_DEBUG] error.response?.data:', error.response?.data);
      // Check if the error response contains session_conflict
      if (error.response?.data?.detail?.error === 'session_conflict') {
        console.log('Session conflict in catch block'); // Debug log
        const detail = error.response.data.detail;
        return { 
          success: false, 
          error: 'session_conflict',
          message: detail.message,
          hint: detail.hint,
          active_since: detail.active_since
        };
      }
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const registerLocal = async (email, password, passcode = null, betaCode = null) => {
    try {
      // Call backend register with beta code
      const response = await apiService.register(email, password, betaCode);
      
      if (response.success) {
        // Store user info locally
        const userData = {
          email: response.email,
          role: response.role,
          passcode,
          email_verified: false
        };
        await healthStorage.saveLocalUser(userData);
        setUser(userData);
        setSessionExpired(false);
        return { success: true, message: response.message };
      } else {
        return { success: false, error: response.message || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = async () => {
    try {
      // Call backend logout to invalidate session
      await apiService.logoutSession();
    } catch (error) {
      console.error('Backend logout failed:', error);
    }
    // Always clear local state
    await healthStorage.deleteLocalUser();
    setUser(null);
    setSessionExpired(false);
  };

  // ========== DELETE ACCOUNT ==========
  
  const deleteAccount = async (password) => {
    try {
      const response = await apiService.deleteAccount(password, 'DELETE');
      if (response.success) {
        // Clear all local data
        await healthStorage.clearAllData();
        setUser(null);
        setHealthRecords([]);
        setConversations([]);
        setStorageStats(null);
        return { success: true };
      }
      return { success: false, error: response.message };
    } catch (error) {
      console.error('Delete account error:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to delete account' };
    }
  };

  // ========== EMAIL VERIFICATION ==========
  
  const sendVerificationEmail = async () => {
    try {
      const response = await apiService.sendVerificationEmail();
      return response;
    } catch (error) {
      console.error('Send verification error:', error);
      throw error;
    }
  };

  const verifyEmail = async (token) => {
    try {
      const response = await apiService.verifyEmail(token);
      if (response.success) {
        // Update local user state
        setUser(prev => prev ? { ...prev, email_verified: true } : null);
      }
      return response;
    } catch (error) {
      console.error('Verify email error:', error);
      throw error;
    }
  };

  // ========== HEALTH RECORDS ==========

  /**
   * Add a new health record to IndexedDB
   * 
   * Simple approach:
   * 1. Save record
   * 2. Update state
   * 3. Backup sync happens automatically on next app load
   * 
   * No complex in-flight backup triggers - just save the data.
   * The backupSyncService checks "do we have unbackuped records?" 
   * on every app load and handles it.
   * 
   * @param {Object} record - Health record to add
   * @returns {Object} The saved record
   */
  const addHealthRecord = async (record) => {
    /*
      Duplicate Detection - Simplified (Jan 25, 2026)
      - Simple filename comparison (KISS principle)
      - If file with same name exists, warn user before upload
      - User can rename file to proceed, or cancel
      - Warns that duplicate data can affect AI commentary accuracy
    */
    
    // Allow bypass for "Replace" functionality
    if (record._bypassDuplicateCheck) {
      delete record._bypassDuplicateCheck;
      const saved = await healthStorage.saveHealthRecord(record);
      // Functional setState — critical (May 19, 2026 fix): when Replace flow
      // calls deleteHealthRecord followed immediately by addHealthRecord(...,
      // bypass=true), the captured `healthRecords` closure here is stale and
      // still contains the about-to-be-deleted record. Using
      // `[saved, ...healthRecords]` would put the old record back into state,
      // showing the user TWO records on screen (the bug Ben reported May 19).
      // Functional updater reads the post-delete state correctly.
      setHealthRecords((prev) => [saved, ...prev.filter((r) => r.id !== saved.id)]);
      await updateStorageStats();
      
      // Backup sync check - also needed for bypass path (Fixed Jan 29, 2026)
      if (sessionToken && user) {
        try {
          const backupResult = await backupSyncService.syncBackupIfNeeded(sessionToken);
          if (backupResult.triggered && backupResult.success) {
            logger.info('[HealthContext] Backup synced (bypass path):', backupResult.records_backed_up, 'records');
            saved._backupResult = backupResult;
          }
        } catch (err) {
          logger.warn('[HealthContext] Backup sync deferred (bypass):', err.message);
        }
      }
      
      return saved;
    }
    
    const newFileName = record.title || record.fileName || '';
    logger.info(`[HealthContext] Checking for duplicate filename: "${newFileName}"`);
    
    // Check BOTH React state AND IndexedDB directly (covers race condition)
    const allRecordsFromDB = await healthStorage.getAllHealthRecords();
    const recordsToCheck = allRecordsFromDB.length > healthRecords.length ? allRecordsFromDB : healthRecords;
    
    // Simple filename match
    const existingRecord = recordsToCheck.find(existing => 
      (existing.title || existing.fileName || '').toLowerCase() === newFileName.toLowerCase()
    );
    
    if (existingRecord) {
      logger.warn(`[HealthContext] Duplicate filename detected: "${newFileName}"`);
      // Return marker so caller can show warning popup
      return { 
        _isDuplicate: true, 
        title: newFileName,
        existingRecord: existingRecord 
      };
    }
    
    const saved = await healthStorage.saveHealthRecord(record);
    // Functional setState mirrors the bypass-path fix above (May 19, 2026).
    setHealthRecords((prev) => [saved, ...prev]);
    await updateStorageStats();

    // Trigger protection prompt for first record. Uses healthRecords closure
    // (pre-save count) — if it was empty, this save makes it 1 total.
    if (healthRecords.length === 0 && storageProtected === false) {
      const alreadyPrompted = await healthStorage.getUserPreferences()
        .then(prefs => prefs.protectionPromptShown);
      
      if (!alreadyPrompted) {
        setShowProtectionPrompt(true);
      }
    }
    
    // Backup sync check - simple: do we have unbackuped records?
    // This catches the upload immediately if session is valid
    // If session times out, next app load will catch it
    if (sessionToken && user) {
      try {
        const backupResult = await backupSyncService.syncBackupIfNeeded(sessionToken);
        if (backupResult.triggered && backupResult.success) {
          logger.info('[HealthContext] Backup synced:', backupResult.records_backed_up, 'records');
          saved._backupResult = backupResult;
        }
      } catch (err) {
        // Silent fail - backup will be caught on next app load
        logger.warn('[HealthContext] Backup sync deferred:', err.message);
      }
    }
    
    return saved;
  };
  
  // Request persistent storage protection
  // PWA Phase 0 (May 7, 2026): logs grant/deny to governance_events.
  // See PWA_DESIGN.md §Implementation Phases (Phase 0).
  const requestStorageProtection = async () => {
    const granted = await healthStorage.requestPersistentStorage();
    setStorageProtected(granted);
    setShowProtectionPrompt(false);
    
    // Mark that we've shown the prompt
    await healthStorage.updateUserPreferences({ protectionPromptShown: true });

    // Governance audit trail. Fire-and-forget; failure never blocks the flow.
    _logPwaEvent(
      granted ? 'pwa_storage_granted' : 'pwa_storage_denied',
      sessionToken,
    );

    return granted;
  };
  
  // Dismiss protection prompt without enabling
  const dismissProtectionPrompt = async () => {
    setShowProtectionPrompt(false);
    await healthStorage.updateUserPreferences({ protectionPromptShown: true });
    // PWA Phase 0: log the user-initiated dismissal so the governance board
    // can distinguish "browser denied" from "user said not now".
    _logPwaEvent('pwa_storage_denied', sessionToken, { reason: 'user_dismissed' });
  };
  
  // Trigger protection prompt (e.g., after restore from backup)
  const triggerProtectionPrompt = async () => {
    // Only show if not already protected
    if (!storageProtected) {
      setShowProtectionPrompt(true);
    }
  };

  const updateHealthRecord = async (id, updates) => {
    const updated = await healthStorage.updateHealthRecord(id, updates);
    setHealthRecords(prev => prev.map(r => r.id === id ? updated : r));
    await updateStorageStats();
    return updated;
  };

  const getHealthRecord = async (id) => {
    return await healthStorage.getHealthRecord(id);
  };

  const deleteHealthRecord = async (id) => {
    await healthStorage.deleteHealthRecord(id);
    setHealthRecords(prev => prev.filter(r => r.id !== id));
    await updateStorageStats();
  };

  const searchHealthRecords = async (query) => {
    return await healthStorage.searchRecords(query);
  };

  const getRecordsByCategory = async (categoryId) => {
    return await healthStorage.getRecordsByCategory(categoryId);
  };

  // ========== CONVERSATIONS ==========

  const addConversation = async (conversation) => {
    const saved = await healthStorage.saveConversation(conversation);
    setConversations(prev => [saved, ...prev]);
    await updateStorageStats();
    return saved;
  };

  const deleteConversation = async (id) => {
    await healthStorage.deleteConversation(id);
    setConversations(prev => prev.filter(c => c.id !== id));
    await updateStorageStats();
  };
  
  /**
   * Get recent conversations formatted for AI context
   * @param {number} limit - Number of recent Q&A pairs to include (default: 3)
   * @returns {string} Formatted conversation history for LLM context
   */
  const getRecentConversationsForContext = async (limit = 3) => {
    try {
      // Get conversations from state (already sorted newest first)
      const recent = conversations.slice(0, limit);
      
      console.log('[Conversation Memory] Getting recent conversations:', {
        totalInState: conversations.length,
        requestedLimit: limit,
        gotCount: recent.length
      });
      
      if (recent.length === 0) {
        console.log('[Conversation Memory] No conversations available for context');
        return '';
      }
      
      // Format for LLM context (oldest first for natural flow)
      const formatted = recent
        .slice()
        .reverse()
        .map((conv, i) => {
          const answerPreview = conv.answer?.substring(0, 500) || '';
          return `[Exchange ${i + 1}]\nUser asked: ${conv.query}\nDr. Ben answered: ${answerPreview}${conv.answer?.length > 500 ? '...' : ''}`;
        })
        .join('\n\n');
      
      const result = `Previous conversation with this user:\n\n${formatted}`;
      console.log('[Conversation Memory] Formatted context length:', result.length, 'chars');
      return result;
    } catch (error) {
      console.error('[Conversation Memory] Failed to get recent conversations:', error);
      return '';
    }
  };

  // ========== AI QUERY ==========

  const queryHealth = async (query, relatedRecordIds = [], conversationContext = null, viewpoint = null, source = null) => {
    try {
      // Build context from related records
      let context = '';
      if (relatedRecordIds.length > 0) {
        const relatedRecords = healthRecords.filter(r => relatedRecordIds.includes(r.id));
        
        console.log('Selected record IDs:', relatedRecordIds);
        console.log('Found related records:', relatedRecords.length);
        
        if (relatedRecords.length > 0) {
          context = relatedRecords.map(r => {
            console.log('Processing record:', r.title, {
              hasOcrText: !!r.ocrText,
              ocrTextLength: r.ocrText?.length || 0,
              hasAnalysis: !!r.analysis,
              hasNotes: !!r.notes
            });
            
            let recordContext = `[Document: ${r.title || r.fileName || 'Untitled'}]`;
            recordContext += `\nCategory: ${r.category || 'Unknown'}`;
            if (r.date) recordContext += `\nDate: ${r.date}`;
            
            // Include OCR text if available
            if (r.ocrText && r.ocrText.trim()) {
              recordContext += `\nExtracted Text:\n${r.ocrText}`;
            }
            
            // Include analysis results if available
            if (r.analysis) {
              if (r.analysis.summary) {
                recordContext += `\nAnalysis Summary: ${r.analysis.summary}`;
              }
              if (r.analysis.findings && r.analysis.findings.length > 0) {
                recordContext += `\nFindings: ${r.analysis.findings.join(', ')}`;
              }
              if (r.analysis.metrics && r.analysis.metrics.length > 0) {
                recordContext += `\nMetrics: ${JSON.stringify(r.analysis.metrics)}`;
              }
            }
            
            // Include notes if available
            if (r.notes && r.notes.trim()) {
              recordContext += `\nNotes: ${r.notes}`;
            }
            
            return recordContext;
          }).join('\n\n---\n\n');
          
          // Add header explaining the context with multi-record awareness
          if (relatedRecords.length > 1) {
            context = `The user has selected ${relatedRecords.length} health records for cross-reference analysis. Compare findings across documents, identify trends, correlations, and changes over time. Highlight agreements and discrepancies between records.\n\n${context}`;
          } else {
            context = `The user has selected the following health record as relevant context:\n\n${context}`;
          }
        }
      }

      // Add conversation context if provided (Option 3: User-controlled conversation memory)
      if (conversationContext) {
        const conversationSection = `\n\n---\n\n[Recent Conversation Context]\n${conversationContext}`;
        context = context ? context + conversationSection : conversationSection;
      }

      // Add final instruction
      if (context) {
        context += '\n\n---\n\nBased on the above context, please answer the user\'s question.';
      }

      console.log('Final context being sent:', context ? context.substring(0, 500) + '...' : 'NO CONTEXT');

      // If no context from records, inform the AI
      if (!context && relatedRecordIds.length > 0) {
        context = "Note: The user selected some health records but they don't contain extractable text. Please ask the user to describe the content or provide more details.";
      }

      // ── Show-and-tell redaction review gate (main chat only, not timeline) ──
      // Preview what the client-side engine will strip from the user's message,
      // let them review/edit it, and only send on confirm.
      let outboundQuery = query;
      let queryAlreadyRedacted = false;
      if (source !== 'timeline' && isReviewGateEnabled()) {
        const preview = redactText(query);
        if (preview.totalRedacted > 0) {
          const decision = await openRedactionReview(preview.redactedText, preview.blockedFragments || []);
          if (decision == null) {
            logPrivacyEvent('review_cancelled');
            const cancelErr = new Error('REDACTION_REVIEW_CANCELLED');
            cancelErr.cancelled = true;
            throw cancelErr;
          }
          outboundQuery = decision;
          queryAlreadyRedacted = true;
          logPrivacyEvent('question_redacted', {
            counts: Object.fromEntries((preview.tokens || []).map(t => [t.type, t.count])),
            total: preview.totalRedacted, review_shown: true, review_edited: decision !== preview.redactedText,
          });
        }
      }

      // Send to backend
      const response = await apiService.queryHealth(outboundQuery, context || null, viewpoint, source, { queryAlreadyRedacted });
      
      // Save conversation
      const conversation = {
        query,
        answer: response.answer,
        modelUsed: response.model_used,
        queryType: response.query_type,
        relatedRecordIds,
        timestamp: new Date().toISOString()
      };
      
      await addConversation(conversation);
      
      return response;
    } catch (error) {
      console.error('Query failed:', error);
      throw error;
    }
  };

  // ========== STORAGE MANAGEMENT ==========

  const updateStorageStats = async () => {
    const stats = await healthStorage.getStorageStats();
    setStorageStats(stats);
  };
  
  // Reload all data from IndexedDB into React state (used after backup restore)
  const reloadAllData = async () => {
    try {
      console.log('[HealthContext] Reloading all data after restore...');
      
      // Reload health records
      const records = await healthStorage.getAllHealthRecords();
      setHealthRecords(records);
      
      // Reload conversations
      const convs = await healthStorage.getAllConversations();
      setConversations(convs);
      
      // Reload storage stats
      const stats = await healthStorage.getStorageStats();
      setStorageStats(stats);
      
      // Reload user preferences
      const prefs = await healthStorage.getUserPreferences();
      setUserPreferences(prefs);
      
      console.log('[HealthContext] Reloaded:', records.length, 'records,', convs.length, 'conversations');
    } catch (error) {
      console.error('[HealthContext] Failed to reload data:', error);
    }
  };

  const exportData = async () => {
    return await healthStorage.exportAllData();
  };

  const importData = async (data) => {
    await healthStorage.importData(data);
    await initializeApp();
  };

  const clearAllData = async () => {
    await healthStorage.clearAllData();
    setHealthRecords([]);
    setConversations([]);
    setUser(null);
    setStorageStats(null);
    setUserPreferences({
      preferredName: '',
      firstVisitComplete: false,
      visitCount: 0
    });
  };

  // ========== USER PREFERENCES (Personalization) ==========
  
  const updatePreferredName = async (name) => {
    const updated = await healthStorage.setPreferredName(name);
    setUserPreferences(updated);
    return updated;
  };
  
  const markFirstVisitComplete = async () => {
    const updated = await healthStorage.markFirstVisitComplete();
    setUserPreferences(updated);
    return updated;
  };
  
  // Update user's avatar
  const updateUserAvatar = async (avatar) => {
    const updated = await healthStorage.setUserAvatar(avatar);
    setUserPreferences(updated);
    return updated;
  };

  const value = {
    user,
    healthRecords,
    conversations,
    storageStats,
    loading,
    sessionToken,
    sessionExpired,
    loggedOutReason,
    userPreferences,
    showProtectionPrompt,
    storageProtected,
    storageOptimized,  // Self-Healing Storage notification (Feb 3, 2026)
    clearLoggedOutReason: () => setLoggedOutReason(null),
    dismissStorageOptimized: () => setStorageOptimized(false),
    loginLocal,
    registerLocal,
    logout,
    deleteAccount,
    sendVerificationEmail,
    verifyEmail,
    addHealthRecord,
    updateHealthRecord,
    getHealthRecord,
    deleteHealthRecord,
    searchHealthRecords,
    getRecordsByCategory,
    addConversation,
    deleteConversation,
    getRecentConversationsForContext,
    queryHealth,
    reviewRedaction,
    updateStorageStats,
    reloadAllData,
    exportData,
    importData,
    clearAllData,
    updatePreferredName,
    markFirstVisitComplete,
    updateUserAvatar,
    requestStorageProtection,
    dismissProtectionPrompt,
    triggerProtectionPrompt,
    // Backup sync functions
    getBackupStatus: backupSyncService.getBackupStatus,
    forceBackup: () => backupSyncService.forceBackup(sessionToken),
    syncBackup: () => backupSyncService.syncBackupIfNeeded(sessionToken)
  };

  return (
    <HealthContext.Provider value={value}>
      {children}
      <RedactionReviewModal
        open={!!redactionGate}
        redactedText={redactionGate?.redactedText}
        removedItems={redactionGate?.removedItems || []}
        onConfirm={(edited) => resolveRedactionReview(edited)}
        onCancel={() => resolveRedactionReview(null)}
      />
    </HealthContext.Provider>
  );
};
